import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, Copy, Loader2, Video } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/utils/cn";
import { PrimaryButton, SecondaryButton } from "../wizard/visuals";
import { fetchTraining, fetchTrainingSlots, purchaseTraining, scheduleTraining, trainingQueryKey } from "./trainingApi";

/**
 * "Treinamento com especialista": 1 hour with a TouchWorks specialist, paid by
 * PIX. The firm picks what the hour covers, pays on the screen, and once the
 * payment lands the calendar opens here to book it. Every state comes from the
 * API, so closing and opening again resumes where it was (the PIX, the
 * calendar or the booked hour).
 */

const money = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });

/** The next weekdays, starting tomorrow (São Paulo). */
export function nextWeekdays(count = 10, from = new Date()) {
  const out = [];
  const d = new Date(from);
  while (out.length < count) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00-03:00`).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });

function Offer({ offer, onBuy, buying, error }) {
  const [topics, setTopics] = useState(() => offer.topics.map((t) => t.id));
  const toggle = (id) => setTopics((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  return (
    <div className="flex flex-col gap-4">
      <div role="group" aria-label="Temas do treinamento" className="flex flex-col gap-2.5">
        {offer.topics.map((t) => {
          const on = topics.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(t.id)}
              className={cn(
                "flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
                on ? "border-foreground ring-1 ring-foreground" : "border-black/[.1] hover:border-foreground/40 dark:border-white/15",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
                  on ? "border-foreground bg-foreground text-background" : "border-black/20 dark:border-white/25",
                )}
              >
                {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
              <span>
                <span className="block text-[15px] font-semibold text-foreground">{t.label}</span>
                <span className="mt-0.5 block text-[13px] text-muted-foreground">{t.description}</span>
              </span>
            </button>
          );
        })}
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      <div className="flex items-center justify-between gap-3 border-t border-black/[.06] pt-4 dark:border-white/[.08]">
        <span className="text-sm text-muted-foreground">Pagamento único por PIX</span>
        <PrimaryButton onClick={() => onBuy(topics)} disabled={!topics.length || buying} className="min-h-[44px] px-6 text-sm">
          {buying ? "Gerando PIX..." : `Gerar PIX de ${money(offer.price)}`}
        </PrimaryButton>
      </div>
    </div>
  );
}

function Pix({ purchase }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(purchase.pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the field stays selectable */
    }
  };
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      {purchase.pix?.image ? (
        <img src={`data:image/png;base64,${purchase.pix.image}`} alt="QR Code do PIX" className="h-48 w-48 rounded-xl bg-white p-2" />
      ) : (
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
      )}
      {purchase.pix?.payload && (
        <div className="flex w-full gap-2">
          <label htmlFor="pix-payload" className="sr-only">
            PIX copia e cola
          </label>
          <input
            id="pix-payload"
            readOnly
            value={purchase.pix.payload}
            onFocus={(e) => e.target.select()}
            className="min-h-[44px] min-w-0 flex-1 rounded-xl border border-black/[.1] bg-transparent px-3 font-mono text-xs text-foreground dark:border-white/15"
          />
          <SecondaryButton onClick={copy} className="min-h-[44px] gap-1.5">
            {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            {copied ? "Copiado" : "Copiar"}
          </SecondaryButton>
        </div>
      )}
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Assim que o pagamento cair, a agenda abre aqui.
      </p>
    </div>
  );
}

function Calendar({ accountId, onBooked }) {
  const { user } = useAuth();
  const days = useMemo(() => nextWeekdays(10), []);
  const [date, setDate] = useState(days[0]);
  const [time, setTime] = useState(null);
  const [contact, setContact] = useState({ name: user?.fullName || "", email: user?.email || "", phone: "" });
  const { data, isLoading, error } = useQuery({
    queryKey: ["training-slots", accountId, date],
    queryFn: () => fetchTrainingSlots(accountId, date),
  });
  const book = useMutation({ mutationFn: () => scheduleTraining(accountId, { date, time, ...contact }), onSuccess: onBooked });
  const slots = data?.slots || [];

  return (
    <div className="flex flex-col gap-4">
      <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
        <Check className="h-4 w-4" aria-hidden="true" />
        Pagamento confirmado. Escolha o melhor horário.
      </p>
      <div role="group" aria-label="Dia" className="flex gap-2 overflow-x-auto pb-1">
        {days.map((d) => (
          <button
            key={d}
            type="button"
            aria-pressed={date === d}
            onClick={() => {
              setDate(d);
              setTime(null);
            }}
            className={cn(
              "min-h-[40px] shrink-0 rounded-full border px-3.5 text-sm capitalize",
              date === d ? "border-foreground bg-foreground font-semibold text-background" : "border-black/[.12] text-foreground dark:border-white/15",
            )}
          >
            {dayLabel(d)}
          </button>
        ))}
      </div>
      <div role="group" aria-label="Horário" className="flex min-h-[44px] flex-wrap gap-2">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : error ? (
          <ErrorLine>{error.message}</ErrorLine>
        ) : slots.length ? (
          slots.map((s) => (
            <button
              key={s.time}
              type="button"
              aria-pressed={time === s.time}
              onClick={() => setTime(s.time)}
              className={cn(
                "min-h-[40px] rounded-xl border px-4 text-sm tabular-nums",
                time === s.time ? "border-foreground bg-foreground font-semibold text-background" : "border-black/[.12] text-foreground dark:border-white/15",
              )}
            >
              {s.time}
            </button>
          ))
        ) : (
          <span className="text-sm text-muted-foreground">Sem horários neste dia. Escolha outro.</span>
        )}
      </div>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {[
          ["name", "Seu nome"],
          ["email", "E-mail para o convite"],
          ["phone", "WhatsApp"],
        ].map(([key, label]) => (
          <label key={key} className="text-[13px] text-muted-foreground">
            {label}
            <input
              value={contact[key]}
              onChange={(e) => setContact((c) => ({ ...c, [key]: e.target.value }))}
              type={key === "email" ? "email" : "text"}
              className="mt-1 block min-h-[40px] w-full rounded-xl border border-black/[.1] bg-transparent px-3 text-sm text-foreground dark:border-white/15"
            />
          </label>
        ))}
      </div>
      {book.error && <ErrorLine>{book.error.message}</ErrorLine>}
      <div className="flex justify-end border-t border-black/[.06] pt-4 dark:border-white/[.08]">
        <PrimaryButton onClick={() => book.mutate()} disabled={!time || !contact.email.trim() || book.isPending} className="min-h-[44px] px-6 text-sm">
          {book.isPending ? "Marcando..." : "Confirmar horário"}
        </PrimaryButton>
      </div>
    </div>
  );
}

function Booked({ purchase, onClose }) {
  const when = purchase.scheduledAt
    ? new Date(purchase.scheduledAt).toLocaleString("pt-BR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div className="flex flex-col items-start gap-3" role="status">
      <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
        <Check className="h-5 w-5" strokeWidth={2.5} />
      </span>
      <h3 className="text-lg font-semibold text-foreground">Treinamento marcado</h3>
      {when && <p className="text-[15px] capitalize text-foreground">{when}</p>}
      <p className="text-sm text-muted-foreground">O convite foi para o seu e-mail.</p>
      <div className="mt-1 flex gap-3">
        {purchase.meetLink && (
          <a
            href={purchase.meetLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-foreground px-5 text-sm font-semibold text-background"
          >
            <Video className="h-4 w-4" aria-hidden="true" />
            Link da reunião
          </a>
        )}
        <SecondaryButton onClick={onClose}>Fechar</SecondaryButton>
      </div>
    </div>
  );
}

function ErrorLine({ children }) {
  return (
    <p role="alert" className="flex items-start gap-2 rounded-xl bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-200">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}

export default function TrainingDialog({ accountId, onClose }) {
  const queryClient = useQueryClient();
  const key = trainingQueryKey(accountId);
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => fetchTraining(accountId),
    // While the PIX is open, ask until the payment lands.
    refetchInterval: (query) => (query.state.data?.purchase?.status === "pending" ? 5000 : false),
  });
  const buy = useMutation({
    mutationFn: (topics) => purchaseTraining(accountId, topics),
    onSuccess: (res) => queryClient.setQueryData(key, res),
  });
  const purchase = data?.purchase || null;
  const status = purchase?.status || "offer";

  const subtitle = {
    offer: "1 hora ao vivo com um especialista da TouchWorks. Escolha o que vamos ver.",
    pending: "Pague com o PIX abaixo. A agenda abre assim que o pagamento cair.",
    paid: "Escolha o dia e o horário da sua hora com o especialista.",
    scheduled: "Até lá!",
  }[status];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] w-[95vw] overflow-y-auto border-black/[.08] bg-background p-6 sm:max-w-[560px] dark:border-white/10">
        <DialogHeader className="mb-2 text-left">
          <DialogTitle className="text-xl">
            Treinamento com especialista{data?.offer ? ` · ${money(data.offer.price)}` : ""}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
          </div>
        ) : error ? (
          <ErrorLine>{error.message}</ErrorLine>
        ) : status === "offer" ? (
          <Offer offer={data.offer} onBuy={(topics) => buy.mutate(topics)} buying={buy.isPending} error={buy.error?.message} />
        ) : status === "pending" ? (
          <Pix purchase={purchase} />
        ) : status === "paid" ? (
          data.calendarReady ? (
            <Calendar accountId={accountId} onBooked={() => queryClient.invalidateQueries({ queryKey: key })} />
          ) : (
            <p className="text-sm text-foreground">Pagamento confirmado. Nossa equipe vai te chamar para marcar o horário.</p>
          )
        ) : (
          <Booked purchase={purchase} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}
